import { create } from "zustand";
import {
  movieImportService,
  MovieImportStatus,
} from "@/services/movieImportService";
import { SyncLog } from "@/types/kkmovie";

interface MovieImportState {
  importStatus: MovieImportStatus | null;
  importHistory: SyncLog[];
  historyPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  isImporting: boolean;
  error: string | null;

  fetchImportStatus: () => Promise<void>;
  fetchImportHistory: (page?: number, limit?: number) => Promise<void>;
  triggerImportAll: (source: "KKphim" | "OPhim") => Promise<boolean>;
  triggerDailyImport: () => Promise<boolean>;
  stopImport: () => Promise<boolean>;
}

export const useMovieImportStore = create<MovieImportState>()((set, get) => ({
  importStatus: null,
  importHistory: [],
  historyPagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
  isLoading: false,
  isImporting: false,
  error: null,

  fetchImportStatus: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await movieImportService.getImportStatus();
      if (res.ok && res.data) {
        const responseData = res.data as any;
        const data =
          responseData?.data?.data || responseData?.data || responseData;
        set({ importStatus: data as MovieImportStatus, isLoading: false });
      } else {
        set({
          isLoading: false,
          error:
            (res as any)?.data?.message || "Lỗi khi tải trạng thái import",
        });
      }
    } catch (e) {
      set({
        isLoading: false,
        error:
          e instanceof Error ? e.message : "Lỗi khi tải trạng thái import",
      });
    }
  },

  fetchImportHistory: async (page = 1, limit = 20) => {
    set({ isLoading: true, error: null });
    try {
      const res = await movieImportService.getImportHistory(page, limit);
      if (res.ok && res.data) {
        const data: any = res.data.data;
        set({
          importHistory: data?.data || [],
          historyPagination: data?.pagination || {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 0,
          },
          isLoading: false,
        });
      } else {
        set({
          isLoading: false,
          error:
            (res as any)?.data?.message || "Lỗi khi tải lịch sử import",
        });
      }
    } catch (e) {
      set({
        isLoading: false,
        error:
          e instanceof Error ? e.message : "Lỗi khi tải lịch sử import",
      });
    }
  },

  triggerImportAll: async (source) => {
    set({ isImporting: true, error: null });
    try {
      const res = await movieImportService.triggerImportAll(source);
      if (res.ok) {
        await get().fetchImportStatus();
        set({ isImporting: false });
        return true;
      }
      set({
        isImporting: false,
        error:
          (res as any)?.data?.message || "Lỗi khi trigger import all",
      });
      return false;
    } catch (e) {
      set({
        isImporting: false,
        error:
          e instanceof Error ? e.message : "Lỗi khi trigger import all",
      });
      return false;
    }
  },

  triggerDailyImport: async () => {
    set({ isImporting: true, error: null });
    try {
      const res = await movieImportService.triggerDailyImport();
      if (res.ok) {
        await get().fetchImportStatus();
        set({ isImporting: false });
        return true;
      }
      set({
        isImporting: false,
        error:
          (res as any)?.data?.message || "Lỗi khi trigger daily import",
      });
      return false;
    } catch (e) {
      set({
        isImporting: false,
        error:
          e instanceof Error ? e.message : "Lỗi khi trigger daily import",
      });
      return false;
    }
  },

  stopImport: async () => {
    try {
      const res = await movieImportService.stopImport();
      if (res.ok) {
        await get().fetchImportStatus();
        return true;
      }
      set({
        error: (res as any)?.data?.message || "Lỗi khi dừng import",
      });
      return false;
    } catch (e) {
      set({
        error: e instanceof Error ? e.message : "Lỗi khi dừng import",
      });
      return false;
    }
  },
}));
